import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { Marker, NavigationControl, Map as ReactMap } from 'react-map-gl';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import { debounce } from 'lodash';
import 'mapbox-gl/dist/mapbox-gl.css';

import { useTravelData, useTravelPOSRefs } from 'apiTyped/hooks/travel';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { useV2Filters } from '../context/V2FiltersContext';
import { V2ContextBar, ContextToolbar } from '../components/V2ContextBar';
import { PageHeader } from '../components/PageHeader';
import { Drawer } from '../components/Drawer';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { SearchInput } from '../components/SearchInput';
import { getColorToStatus, getOption } from 'utils/pos';

type ListTab = 'list' | 'itinerary';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiZGMtYW50b2luZSIsImEiOiJjanM1cXNpNzAwZ3ZoM3lzYjZvNjQ3dTJoIn0.BVKVyGXmYOqgvZqDWcMKXA';

type ViewState = {
  latitude: number;
  longitude: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
  transitionDuration?: number | 'auto';
};

type StatusFilter = 'all' | 'pos' | 'lost' | 'todo' | 'other';
type TypeFilter = 'all' | 'ON' | 'OFF';

const STATUS_TO_BADGE: Record<string, 'active' | 'lost' | 'towin' | 'default'> =
  {
    pos: 'active',
    lost: 'lost',
    todo: 'towin',
    other: 'default'
  };

export const V2MarketMap: React.FC = () => {
  const { user } = useUserMe();
  const { filters } = useV2Filters();

  const productType = filters.productType === '2' ? 'spirit' : 'wine';

  // First call to get available markets (pass undefined to get all)
  const {
    isLoading: isLoadingMap,
    availableMarkets,
    selectedMarket,
    pos: pointOfSales
  } = useTravelData({
    market_id: undefined,
    productType: productType as any
  });

  // Resolve market_id from V2 context market name
  const resolvedMarketId = useMemo(() => {
    if (!availableMarkets || availableMarkets.length === 0) return undefined;
    if (!filters.market) return availableMarkets[0]?.market_id;
    const found = availableMarkets.find(
      (m: any) =>
        m.market_name === filters.market ||
        String(m.market_id) === filters.market
    );
    return found?.market_id ?? availableMarkets[0]?.market_id;
  }, [filters.market, availableMarkets]);

  // Fetch POS data for the resolved market
  const { pos: marketPOS } = useTravelData({
    market_id: resolvedMarketId,
    productType: productType as any
  });

  const { pos: posRefsData } = useTravelPOSRefs({
    market_id: resolvedMarketId,
    productType: productType as any
  });

  // Use market-filtered POS if available, otherwise fall back
  const activePOS = resolvedMarketId ? marketPOS : pointOfSales;

  // Map state
  const [viewState, setViewState] = useState<ViewState>({
    latitude: 46.227638,
    longitude: 2.213749,
    zoom: 5
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedPOS, setSelectedPOS] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [listTab, setListTab] = useState<ListTab>('list');
  const [listOpen, setListOpen] = useState(true);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  // Filtered markers
  const filteredMarkers = useMemo(() => {
    let filtered = activePOS.filter((m: any) => m.latitude && m.longitude);

    if (statusFilter !== 'all') {
      filtered = filtered.filter((m: any) => m.state === statusFilter);
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter((m: any) => m.network_code === typeFilter);
    }
    if (search) {
      const regex = new RegExp(search, 'gi');
      filtered = filtered.filter((m: any) => regex.test(m.name));
    }
    return filtered;
  }, [activePOS, statusFilter, typeFilter, search]);

  // Fit bounds when markers change
  useEffect(() => {
    if (filteredMarkers.length > 0 && mapContainerRef.current) {
      const maxLat = Math.max(...filteredMarkers.map((m: any) => m.latitude));
      const maxLng = Math.max(...filteredMarkers.map((m: any) => m.longitude));
      const minLat = Math.min(...filteredMarkers.map((m: any) => m.latitude));
      const minLng = Math.min(...filteredMarkers.map((m: any) => m.longitude));

      const { width, height } = mapContainerRef.current.getBoundingClientRect();
      if (width === 0 || height === 0) return;

      const viewport = new WebMercatorViewport({
        width,
        height,
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        zoom: viewState.zoom
      });

      try {
        const { longitude, latitude, zoom } = viewport.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat]
          ],
          { padding: 40 }
        );
        setViewState({
          latitude,
          longitude,
          zoom: zoom - 1,
          transitionDuration: 1500
        });
      } catch {
        // bounds too narrow
      }
    }
  }, [filteredMarkers]);

  // Resize handler
  useEffect(() => {
    const handleResize = debounce(() => {
      mapRef.current?.resize();
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // Marker click
  const handleMarkerClick = useCallback(
    (marker: any, e?: React.MouseEvent) => {
      e?.stopPropagation();
      const posRef =
        posRefsData.find((p: any) => p.store_id === marker.store_id) || {};
      setSelectedPOS({ ...marker, ...posRef });
      setDrawerOpen(true);
    },
    [posRefsData]
  );

  const handleMapClick = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Build a map of store_id -> refs count
  const refsCountMap = useMemo(() => {
    const map: Record<number, number> = {};
    posRefsData.forEach((p: any) => {
      if (p.client_refs_by_bottle) {
        map[p.store_id] = p.client_refs_by_bottle.length;
      }
    });
    return map;
  }, [posRefsData]);

  // List items sorted by refs count descending
  const listItems = useMemo(() => {
    return [...filteredMarkers].sort((a: any, b: any) => {
      const aRefs = refsCountMap[a.store_id] || 0;
      const bRefs = refsCountMap[b.store_id] || 0;
      return bRefs - aRefs;
    });
  }, [filteredMarkers, refsCountMap]);

  return (
    <Page>
      <PageHeader
        title="Market Map"
        subtitle="Geographic view of every surveyed POS — explore territory coverage, build sales itineraries, export routes to Google Maps."
      />

      <V2ContextBar>
        <ContextToolbar>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            {(['all', 'pos', 'lost', 'todo', 'other'] as StatusFilter[]).map(
              (s) => (
                <PillButton
                  key={s}
                  $active={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'all'
                    ? 'All'
                    : s === 'pos'
                      ? 'Active'
                      : s === 'lost'
                        ? 'Lost'
                        : s === 'todo'
                          ? 'To Win'
                          : 'Other'}
                </PillButton>
              )
            )}
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Type</FilterLabel>
            {(['all', 'ON', 'OFF'] as TypeFilter[]).map((t) => (
              <PillButton
                key={t}
                $active={typeFilter === t}
                onClick={() => setTypeFilter(t)}
              >
                {t === 'all' ? 'All' : t === 'ON' ? 'On Trade' : 'Off Trade'}
              </PillButton>
            ))}
          </FilterGroup>
          <PosCount>{filteredMarkers.length} points of sale</PosCount>
          <SearchInput
            placeholder="Search point of sale..."
            value={search}
            onChange={(val) => setSearch(val)}
          />
        </ContextToolbar>
      </V2ContextBar>

      {/* Map + List panel */}
      <MapAndPanel>
        <MapWrapper ref={mapContainerRef}>
          {isLoadingMap && <LoadingOverlay>Loading map data...</LoadingOverlay>}
          <ReactMap
            ref={mapRef}
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/light-v10"
            mapboxAccessToken={MAPBOX_TOKEN}
          >
            {filteredMarkers.map((marker: any, idx: number) => (
              <Marker
                key={`${marker.store_id}-${idx}`}
                latitude={marker.latitude}
                longitude={marker.longitude}
              >
                <MarkerDot
                  $networkCode={marker.network_code}
                  $color={getColorToStatus(marker.state)}
                  onClick={(e) => handleMarkerClick(marker, e)}
                />
              </Marker>
            ))}
            <NavigationControl position="bottom-right" showCompass={false} />
          </ReactMap>
          <MapInfo>
            {filteredMarkers.length} POS in view ·{' '}
            {selectedMarket?.market_name || '—'} · zoom{' '}
            {Math.round(viewState.zoom)}x
          </MapInfo>
        </MapWrapper>

        {/* Right side list panel */}
        <ListPanel $open={listOpen}>
          <ListTabs>
            <ListTabBtn
              $active={listTab === 'list'}
              onClick={() => setListTab('list')}
            >
              ≡ List <ListTabCount>{filteredMarkers.length}</ListTabCount>
            </ListTabBtn>
            <ListTabBtn
              $active={listTab === 'itinerary'}
              onClick={() => setListTab('itinerary')}
            >
              {'>'} Itinerary
            </ListTabBtn>
          </ListTabs>
          <ListBody>
            {listTab === 'list' && (
              <>
                {listItems.map((pos: any, idx: number) => {
                  const refs = refsCountMap[pos.store_id] || 0;
                  return (
                    <ListItem
                      key={pos.store_id || idx}
                      onClick={() => handleMarkerClick(pos)}
                      $active={selectedPOS?.store_id === pos.store_id}
                    >
                      <ListItemDot $color={getColorToStatus(pos.state)} />
                      <ListItemInfo>
                        <ListItemName>{pos.name}</ListItemName>
                        <ListItemMeta>
                          {pos.city || pos.address || '—'} ·{' '}
                          {pos.network_code === 'ON' ? 'On Trade' : 'Off Trade'}
                          {pos.category_name && ` · ${pos.category_name}`}
                        </ListItemMeta>
                      </ListItemInfo>
                      {refs > 0 && (
                        <ListItemRefs>
                          <span>{refs}</span>
                          <small>refs</small>
                        </ListItemRefs>
                      )}
                    </ListItem>
                  );
                })}
                {listItems.length === 0 && (
                  <ListEmpty>
                    No points of sale match the current filters.
                  </ListEmpty>
                )}
              </>
            )}
            {listTab === 'itinerary' && (
              <ListEmpty>
                Select points of sale on the map to build an itinerary.
              </ListEmpty>
            )}
          </ListBody>
        </ListPanel>
      </MapAndPanel>

      {/* Detail drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedPOS?.name}
        eyebrow="Point of Sale"
      >
        {selectedPOS && (
          <DrawerContent>
            <DrawerRow>
              <DrawerLabel>Status</DrawerLabel>
              <Badge variant={STATUS_TO_BADGE[selectedPOS.state] || 'default'}>
                {selectedPOS.state === 'pos'
                  ? 'Active'
                  : selectedPOS.state === 'lost'
                    ? 'Lost'
                    : selectedPOS.state === 'todo'
                      ? 'To Win'
                      : selectedPOS.state || '—'}
              </Badge>
            </DrawerRow>
            <DrawerRow>
              <DrawerLabel>Network</DrawerLabel>
              <span>
                {selectedPOS.network_code === 'ON' ? 'On Trade' : 'Off Trade'}
              </span>
            </DrawerRow>
            {selectedPOS.address && (
              <DrawerRow>
                <DrawerLabel>Address</DrawerLabel>
                <span>{selectedPOS.address}</span>
              </DrawerRow>
            )}
            {selectedPOS.phone && (
              <DrawerRow>
                <DrawerLabel>Phone</DrawerLabel>
                <span>{selectedPOS.phone}</span>
              </DrawerRow>
            )}
            {selectedPOS.email && (
              <DrawerRow>
                <DrawerLabel>Email</DrawerLabel>
                <span>{selectedPOS.email}</span>
              </DrawerRow>
            )}
            {selectedPOS.ws_award && (
              <DrawerRow>
                <DrawerLabel>WS Award</DrawerLabel>
                <span>{selectedPOS.ws_award}</span>
              </DrawerRow>
            )}
            {selectedPOS.stars && (
              <DrawerRow>
                <DrawerLabel>Stars</DrawerLabel>
                <span>{'★'.repeat(selectedPOS.stars)}</span>
              </DrawerRow>
            )}
            {selectedPOS.dn_total != null && (
              <DrawerRow>
                <DrawerLabel>DN Total</DrawerLabel>
                <span>{selectedPOS.dn_total}</span>
              </DrawerRow>
            )}
            {selectedPOS.client_refs_by_bottle &&
              selectedPOS.client_refs_by_bottle.length > 0 && (
                <DrawerSection>
                  <DrawerLabel>Your References</DrawerLabel>
                  <RefList>
                    {selectedPOS.client_refs_by_bottle.map(
                      (ref: any, i: number) => (
                        <RefItem key={i}>
                          {ref.name || ref.wine_name || JSON.stringify(ref)}
                        </RefItem>
                      )
                    )}
                  </RefList>
                </DrawerSection>
              )}
          </DrawerContent>
        )}
      </Drawer>
    </Page>
  );
};

// --- Styled Components ---

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 56px);
  gap: 6px;
  overflow: hidden;
  margin: -8px -12px -40px -12px;
  padding: 8px 12px 0;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-shrink: 0;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
`;

const FilterLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-muted);
  margin-right: 4px;
`;

const PillButton = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--terracotta)' : 'var(--line)')};
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--white)'};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink)')};
  transition: all 0.15s;

  &:hover {
    border-color: var(--terracotta);
  }
`;

const PosCount = styled.span`
  margin-left: auto;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-faint);
`;

const MapWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--line);

  .mapboxgl-ctrl-logo,
  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`;

const MapAndPanel = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 0;
`;

const MapInfo = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 253, 249, 0.92);
  padding: 5px 10px;
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--ink-muted);
  pointer-events: none;
  backdrop-filter: blur(4px);
`;

const ListPanel = styled.div<{ $open: boolean }>`
  position: relative;
  width: ${({ $open }) => ($open ? '240px' : '0px')};
  min-width: ${({ $open }) => ($open ? '240px' : '0px')};
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-left: none;
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
  background: var(--white);
  overflow: hidden;
  transition:
    width 0.25s,
    min-width 0.25s;
`;

const ListTabs = styled.div`
  display: flex;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
`;

const ListTabBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 8px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--ink-muted)'};
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? 'var(--terracotta)' : 'transparent')};
  cursor: pointer;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: var(--terracotta);
  }
`;

const ListTabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 9px;
  background: var(--terracotta);
  color: var(--white);
  font-size: 11px;
  font-weight: 600;
`;

const ListBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-xs) 0;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 3px;
  }
`;

const ListItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--paper)' : 'transparent')};
  border-left: 3px solid
    ${({ $active }) => ($active ? 'var(--terracotta)' : 'transparent')};
  transition: background 0.12s;

  &:hover {
    background: var(--paper);
  }
`;

const ListItemDot = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ListItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ListItemName = styled.div`
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListItemMeta = styled.div`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListItemRefs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  span {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
  }
  small {
    font-family: var(--font-sans);
    font-size: 10px;
    color: var(--ink-faint);
  }
`;

const ListEmpty = styled.div`
  padding: 24px 14px;
  text-align: center;
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--ink-muted);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 253, 249, 0.8);
  z-index: 10;
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--ink-muted);
`;

const MarkerDot = styled.div<{ $networkCode: string; $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: ${({ $networkCode }) =>
    $networkCode === 'ON' ? '50%' : '2px'};
  background-color: ${({ $color }) => $color};
  border: 2px solid var(--white);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: transform 0.15s;

  &:hover {
    transform: scale(1.5);
  }
`;

// Drawer content styles
const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
`;

const DrawerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--line-soft);
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--ink);
`;

const DrawerLabel = styled.span`
  color: var(--ink-muted);
  font-size: 12px;
`;

const DrawerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding-top: var(--spacing-sm);
`;

const RefList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RefItem = styled.li`
  font-family: var(--font-sans);
  font-size: 12.5px;
  color: var(--ink);
  padding: 4px 8px;
  background: var(--paper);
  border-radius: var(--radius-sm);
`;
